import { Box, Button, Dialog, Divider, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { HOPE_SANS, HOPE_SERIF } from "@/shared/branding/thehopeonebrand";
import type { ImpactBankDetails } from "../utils/impactCardData";
import {
  IMPACT_CURRENCY,
  formatImpactAmount,
  formatImpactMoney,
} from "../utils/impactCurrency";

/** The line that carries the whole contribution experience. */
export const IMPACT_SLOGAN = "Turn a moment of care into something a child can hold.";

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#5E8F72",
  greenLight: "#8FB49B",
  mist: "#EEF5EF",
  ivory: "#FAF8F2",
  paper: "#FFFFFF",
  muted: "#68766F",
  soft: "#89968F",
  border: "rgba(16,44,36,.10)",
  borderStrong: "rgba(16,44,36,.18)",
};

const tileSx = {
  p: { xs: 1.75, sm: 2.25 },
  borderRadius: "14px",
  bgcolor: c.paper,
  border: `1px solid ${c.border}`,
} as const;

const labelSx = {
  fontFamily: HOPE_SANS,
  fontSize: "0.62rem",
  fontWeight: 800,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  color: c.green,
};

const bodySx = {
  fontFamily: HOPE_SANS,
  fontSize: { xs: "0.82rem", sm: "0.86rem" },
  lineHeight: 1.7,
  color: c.muted,
};

/** Right-hand values in the summary rows: account numbers and names must wrap. */
const detailValueSx = {
  fontFamily: HOPE_SANS,
  fontWeight: 700,
  fontSize: "0.75rem",
  color: c.forest,
  minWidth: 0,
  maxWidth: { xs: "60%", sm: "58%" },
  textAlign: "right" as const,
  overflowWrap: "anywhere" as const,
};

const PRESETS = [10_000, 30_000, 50_000];

type ImpactContributionDialogProps = {
  open?: boolean;
  onClose?: () => void;
  /** Render the same UI inline on the Contribute page instead of a popup. */
  embedded?: boolean;
  /** Cause title, shown in the header band. */
  title: string;
  targetAmount?: number;
  /** Use-of-funds line the contribution is directed to first. */
  firstFundLine?: string;
  beneficiaryName?: string;
  bankDetails?: ImpactBankDetails;
  contributorLabel?: string;
  confirmLabel?: string;
  initialAmount?: number;
  /** Fired after a successful local confirmation when no `onSubmit` is provided. */
  onConfirmed?: (amount: number) => void;
  /** Persist the contribution through POST /main-card/contributions. */
  onSubmit?: (amount: number, message: string) => Promise<void>;
  submitting?: boolean;
};

/**
 * Contribution flow shared by the Impact Card poster, book pages, and back face.
 */
export default function ImpactContributionDialog({
  open = false,
  onClose,
  embedded = false,
  title,
  targetAmount,
  firstFundLine,
  beneficiaryName,
  bankDetails,
  contributorLabel,
  confirmLabel = "Commit to Contribute",
  initialAmount,
  onConfirmed,
  onSubmit,
  submitting = false,
}: ImpactContributionDialogProps) {
  const [amount, setAmount] = useState(String(initialAmount ?? 50_000));
  const [message, setMessage] = useState("");
  const [confirmed, setConfirmed] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const visible = embedded || open;

  // Every fresh open starts back at the amount picker.
  useEffect(() => {
    if (!visible || embedded) return;
    setConfirmed(null);
    setSubmitError(null);
    setMessage("");
    if (initialAmount && initialAmount > 0) setAmount(String(initialAmount));
  }, [embedded, visible, initialAmount]);

  const numericAmount = Number(amount);
  const isValid = Number.isFinite(numericAmount) && numericAmount > 0;

  const shareOfGoal =
    targetAmount && targetAmount > 0 && isValid ? (numericAmount / targetAmount) * 100 : 0;

  const confirm = async () => {
    if (!isValid || submitting) return;
    setSubmitError(null);

    if (onSubmit) {
      try {
        await onSubmit(numericAmount, message);
        setConfirmed(numericAmount);
        onConfirmed?.(numericAmount);
      } catch (error) {
        const axiosMessage = (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message;
        setSubmitError(axiosMessage || (error as Error)?.message || "Contribution failed. Try again.");
      }
      return;
    }

    setConfirmed(numericAmount);
    onConfirmed?.(numericAmount);
  };

  const shellSx = {
    borderRadius: "20px",
    overflow: "hidden",
    bgcolor: c.ivory,
    backgroundImage: "none",
    border: `1px solid ${c.border}`,
    boxShadow: embedded ? "none" : "0 30px 70px rgba(16,44,36,.28)",
  } as const;

  const body = (
    <>
      {/* Header band */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          background: `linear-gradient(150deg, ${c.forestDeep} 0%, ${c.forest} 55%, #295846 100%)`,
          color: "#FFFFFF",
          position: "relative",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.14,
            background: `radial-gradient(circle at 82% 10%, ${c.greenLight}, transparent 58%)`,
          }}
        />

        <Box sx={{ position: "relative" }}>
          <Typography
            sx={{
              fontFamily: HOPE_SANS,
              fontSize: "0.58rem",
              fontWeight: 800,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,.62)",
            }}
          >
            {confirmed ? "Your intention is recorded" : "Stand with this cause"}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontFamily: HOPE_SERIF,
              fontWeight: 700,
              fontSize: { xs: "1.25rem", sm: "1.4rem" },
              lineHeight: 1.3,
            }}
          >
            {confirmed ? "Thank you for showing up." : title}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontFamily: HOPE_SERIF,
              fontStyle: "italic",
              fontSize: "0.86rem",
              lineHeight: 1.55,
              color: "rgba(255,255,255,.78)",
            }}
          >
            {confirmed
              ? "Your name now lives inside this story — and you'll see exactly where it goes."
              : IMPACT_SLOGAN}
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ px: { xs: 2.5, sm: 3 }, py: { xs: 2.5, sm: 3 } }}>
        {confirmed ? (
          <Stack spacing={2}>
            <Box
              sx={{
                ...tileSx,
                textAlign: "center",
                bgcolor: c.mist,
                borderColor: c.borderStrong,
              }}
            >
              <Typography
                sx={{
                  fontFamily: HOPE_SERIF,
                  fontSize: "1.6rem",
                  fontWeight: 700,
                  color: c.forestDeep,
                }}
              >
                {formatImpactMoney(confirmed)}
              </Typography>
              <Typography sx={{ ...bodySx, mt: 0.5, fontSize: "0.75rem" }}>
                pledged toward {firstFundLine ?? "this cause"}
              </Typography>
            </Box>

            <Typography sx={{ ...bodySx, textAlign: "center", fontSize: "0.75rem" }}>
              {onSubmit
                ? "Your contribution is now on this Card. The talent can see who gave, and how much."
                : "This is a preview of the contribution experience. No payment was taken and no funds have moved."}
            </Typography>

            <Button
              fullWidth
              onClick={() => {
                if (embedded) {
                  setConfirmed(null);
                  return;
                }
                onClose?.();
              }}
              sx={{
                borderRadius: "999px",
                py: 1.1,
                textTransform: "none",
                fontFamily: HOPE_SANS,
                fontWeight: 750,
                bgcolor: c.forestDeep,
                color: "#FFFFFF",
                "&:hover": { bgcolor: c.forest },
              }}
            >
              {embedded ? "Make another commitment" : "Back to the cause"}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2.25}>
            <Box>
              <Typography sx={{ ...labelSx, mb: 1 }}>Choose your contribution</Typography>

              {/* Presets wrap rather than squeeze when the dialog is narrow. */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {PRESETS.map((value) => {
                  const active = amount === String(value);
                  return (
                    <Box
                      key={value}
                      role="button"
                      tabIndex={0}
                      onClick={() => setAmount(String(value))}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setAmount(String(value));
                        }
                      }}
                      sx={{
                        flex: { xs: "1 1 45%", sm: "1 1 0" },
                        minWidth: 0,
                        py: 1.25,
                        px: 0.5,
                        borderRadius: "12px",
                        textAlign: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        transition: "all .18s ease",
                        bgcolor: active ? c.forestDeep : c.paper,
                        color: active ? "#FFFFFF" : c.forest,
                        border: `1px solid ${active ? c.forestDeep : c.border}`,
                        boxShadow: active ? "0 8px 18px rgba(16,44,36,.18)" : "none",
                        "&:hover": {
                          borderColor: active ? c.forestDeep : c.green,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: HOPE_SERIF,
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          lineHeight: 1.2,
                        }}
                      >
                        ₩{formatImpactAmount(value)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: HOPE_SANS,
                          fontSize: "0.58rem",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          opacity: active ? 0.75 : 0.6,
                        }}
                      >
                        {IMPACT_CURRENCY}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <TextField
              fullWidth
              size="small"
              label={`Or enter an amount (${IMPACT_CURRENCY})`}
              type="number"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputProps={{ min: 1 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: c.paper,
                  fontFamily: HOPE_SANS,
                  "& fieldset": { borderColor: c.border },
                  "&:hover fieldset": { borderColor: c.green },
                  "&.Mui-focused fieldset": { borderColor: c.forest },
                },
                "& .MuiInputLabel-root": {
                  fontFamily: HOPE_SANS,
                  fontSize: "0.85rem",
                  color: c.soft,
                },
                "& .MuiInputLabel-root.Mui-focused": { color: c.forest },
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="A short message (optional)"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              inputProps={{ maxLength: 280 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: c.paper,
                  fontFamily: HOPE_SANS,
                  "& fieldset": { borderColor: c.border },
                  "&:hover fieldset": { borderColor: c.green },
                  "&.Mui-focused fieldset": { borderColor: c.forest },
                },
                "& .MuiInputLabel-root": {
                  fontFamily: HOPE_SANS,
                  fontSize: "0.85rem",
                  color: c.soft,
                },
                "& .MuiInputLabel-root.Mui-focused": { color: c.forest },
              }}
            />

            {/* What this gift does */}
            <Box sx={{ ...tileSx, bgcolor: c.mist, borderColor: c.borderStrong }}>
              <Typography sx={labelSx}>What this does</Typography>

              <Stack spacing={0.85} sx={{ mt: 1.1 }}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>Share of the goal</Typography>
                  <Typography
                    sx={{
                      fontFamily: HOPE_SERIF,
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      color: c.forestDeep,
                    }}
                  >
                    {shareOfGoal >= 0.1 ? `${shareOfGoal.toFixed(1)}%` : "—"}
                  </Typography>
                </Stack>

                <Divider sx={{ borderColor: c.border }} />

                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>Goes first to</Typography>
                  <Typography sx={detailValueSx}>
                    {firstFundLine ?? "the card creator's account"}
                  </Typography>
                </Stack>

                {beneficiaryName ? (
                  <>
                    <Divider sx={{ borderColor: c.border }} />
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>Then allocated to</Typography>
                      <Typography sx={detailValueSx}>{beneficiaryName}</Typography>
                    </Stack>
                  </>
                ) : null}

                {contributorLabel ? (
                  <>
                    <Divider sx={{ borderColor: c.border }} />
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>Contributor</Typography>
                      <Typography sx={detailValueSx}>{contributorLabel}</Typography>
                    </Stack>
                  </>
                ) : null}

                {bankDetails?.bankName || bankDetails?.accountNumber ? (
                  <>
                    <Divider sx={{ borderColor: c.border }} />
                    {bankDetails.bankName ? (
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>Bank</Typography>
                        <Typography sx={detailValueSx}>{bankDetails.bankName}</Typography>
                      </Stack>
                    ) : null}
                    {bankDetails.accountHolder ? (
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>Account holder</Typography>
                        <Typography sx={detailValueSx}>{bankDetails.accountHolder}</Typography>
                      </Stack>
                    ) : null}
                    {bankDetails.accountNumber ? (
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>Account number</Typography>
                        <Typography sx={detailValueSx}>{bankDetails.accountNumber}</Typography>
                      </Stack>
                    ) : null}
                    {bankDetails.swiftCode ? (
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>SWIFT</Typography>
                        <Typography sx={detailValueSx}>{bankDetails.swiftCode}</Typography>
                      </Stack>
                    ) : null}
                    {bankDetails.reference ? (
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography sx={{ ...bodySx, fontSize: "0.78rem" }}>Reference</Typography>
                        <Typography sx={detailValueSx}>{bankDetails.reference}</Typography>
                      </Stack>
                    ) : null}
                    {bankDetails.instructions ? (
                      <Typography sx={{ ...bodySx, fontSize: "0.75rem" }}>{bankDetails.instructions}</Typography>
                    ) : null}
                  </>
                ) : null}
              </Stack>
            </Box>

            {submitError ? (
              <Typography sx={{ ...bodySx, color: "#9B3A3A", fontSize: "0.75rem" }}>
                {submitError}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1.25}>
              {embedded ? null : (
              <Button
                onClick={onClose}
                sx={{
                  flexShrink: 0,
                  borderRadius: "999px",
                  px: 2,
                  textTransform: "none",
                  fontFamily: HOPE_SANS,
                  fontWeight: 700,
                  color: c.muted,
                  "&:hover": { bgcolor: "rgba(16,44,36,.05)" },
                }}
              >
                Not now
              </Button>
              )}

              <Button
                fullWidth
                disabled={!isValid || submitting}
                onClick={() => {
                  void confirm();
                }}
                sx={{
                  borderRadius: "999px",
                  py: 1.1,
                  textTransform: "none",
                  fontFamily: HOPE_SANS,
                  fontWeight: 750,
                  background: `linear-gradient(135deg, ${c.forestDeep} 0%, ${c.forest} 55%, #376B55 100%)`,
                  color: "#FFFFFF",
                  boxShadow: "0 10px 24px rgba(16,44,36,.20)",
                  "&:hover": {
                    background: `linear-gradient(135deg, #17382E 0%, #295846 55%, #4F8066 100%)`,
                  },
                  "&.Mui-disabled": { opacity: 0.45, color: "rgba(255,255,255,.85)" },
                }}
              >
                {submitting
                  ? "Sending…"
                  : confirmLabel}
              </Button>
            </Stack>

            <Typography
              sx={{
                ...bodySx,
                fontSize: "0.66rem",
                textAlign: "center",
                color: c.soft,
              }}
            >
              {onSubmit
                ? "This records a commitment only. Transfer the amount to the bank details shown. It will not count toward verified progress until the card creator confirms receipt."
                : "Preview only — no payment is processed and no funds move."}
            </Typography>
          </Stack>
        )}
      </Box>
    </>
  );

  if (embedded) {
    return <Box sx={shellSx}>{body}</Box>;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: shellSx }}>
      {body}
    </Dialog>
  );
}
